import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment/locale/pt-br";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./ReservasCalendarSidebar.css";
import { Reserva, Cliente } from "../../types";
import { formatNumeroReserva } from "../../utils/reservaUtils";
import { FaEye, FaWhatsapp, FaSearch, FaSync } from "react-icons/fa";
import { Button } from "../../app/components";
import { DataList, DataListColumn } from "../Common/DataList";
import { Pagination } from "../Common/Pagination";
import Badge from "react-bootstrap/Badge";
import Form from "react-bootstrap/Form";
import { Hash } from "lucide-react";

// Configurar moment para português brasileiro
moment.locale("pt-br");
const localizer = momentLocalizer(moment);

interface ReservasCalendarSidebarProps {
  reservas: Reserva[];
  clientes: Cliente[];
  onViewReserva: (reserva: Reserva) => void;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: {
    reserva: Reserva;
    cliente: Cliente | null;
  };
}

const ReservasCalendarSidebar: React.FC<ReservasCalendarSidebarProps> = ({
  reservas,
  clientes,
  onViewReserva,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const today = new Date();
    // Garantir que a data seja válida
    return isNaN(today.getTime()) ? new Date() : today;
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  // Função wrapper para setSelectedDate com validação
  const setSelectedDateSafe = useCallback((date: Date) => {
    try {
      if (date && date instanceof Date && !isNaN(date.getTime())) {
        setSelectedDate(date);
      } else {
        console.warn("setSelectedDateSafe: data inválida recebida", date);
      }
    } catch (error) {
      console.error("Erro em setSelectedDateSafe:", error);
    }
  }, []);

  // Wrapper para o Calendar que captura erros internos
  const SafeCalendar = useCallback((props: any) => {
    try {
      return <Calendar {...props} />;
    } catch (error) {
      console.error("Erro no Calendar:", error);
      return (
        <div className="text-center text-muted py-5">
          <div className="mb-3">
            <i className="fas fa-calendar-times fa-3x text-muted"></i>
          </div>
          <p className="mb-0">Erro ao carregar calendário</p>
        </div>
      );
    }
  }, []);

  // Calendário limpo - sem eventos visíveis
  const events: CalendarEvent[] = useMemo(() => {
    return []; // Array vazio - calendário completamente limpo
  }, []);

  // Filtrar reservas do dia selecionado
  const reservasDoDia = useMemo(() => {
    const dataSelecionada = moment(selectedDate).format("YYYY-MM-DD");

    let reservasFiltradas = reservas.filter((reserva) => {
      const dataRetirada = moment(reserva.dataRetirada).format("YYYY-MM-DD");
      return dataRetirada === dataSelecionada;
    });

    // Aplicar filtro de busca
    if (searchTerm) {
      const termoBusca = searchTerm.toLowerCase();
      reservasFiltradas = reservasFiltradas.filter(
        (reserva) =>
          reserva.clienteNome?.toLowerCase().includes(termoBusca) ||
          reserva.numeroReserva?.toLowerCase().includes(termoBusca) ||
          reserva.observacoes?.toLowerCase().includes(termoBusca),
      );
    }

    // Aplicar filtro de status
    if (statusFilter) {
      reservasFiltradas = reservasFiltradas.filter(
        (reserva) => reserva.status === statusFilter,
      );
    }

    return reservasFiltradas;
  }, [reservas, selectedDate, searchTerm, statusFilter]);

  const totalPages = Math.ceil(reservasDoDia.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReservas = reservasDoDia.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDate, searchTerm, statusFilter, itemsPerPage]);

  // Função chamada quando clica em um dia
  const handleSelectSlot = (slotInfo: any) => {
    try {
      // Validar se slotInfo e start existem
      if (!slotInfo || !slotInfo.start) {
        console.warn("handleSelectSlot: slotInfo ou start inválido", slotInfo);
        return;
      }

      // Verificar se start é uma data válida
      if (
        !(slotInfo.start instanceof Date) ||
        isNaN(slotInfo.start.getTime())
      ) {
        console.warn(
          "handleSelectSlot: start não é uma data válida",
          slotInfo.start,
        );
        return;
      }

      setSelectedDateSafe(slotInfo.start);
    } catch (error) {
      console.error("Erro em handleSelectSlot:", error);
    }
  };

  // Função para renderizar a célula do dia com destaque personalizado
  const dayPropGetter = (date: Date) => {
    const isSelected = moment(date).isSame(selectedDate, "day");
    const isToday = moment(date).isSame(moment(), "day");

    if (isSelected) {
      return {
        style: {
          backgroundColor: "#0abb83",
          color: "white",
  
        },
      };
    }

    if (isToday) {
      return {
        style: {
          backgroundColor: "#e3f2fd",
          fontWeight: "600",
        },
      };
    }

    return {};
  };

  // Função para formatar data
  const formatDate = (dateString: string) => {
    return moment(dateString).format("DD/MM/YYYY");
  };

  // Função para obter badge de status
  const getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { variant: string; text: string } } = {
      reservado: { variant: "warning", text: "Reservado" },
      retirado: { variant: "info", text: "Retirado" },
      devolvido: { variant: "success", text: "Devolvido" },
      cancelado: { variant: "danger", text: "Cancelado" },
    };

    const config = statusConfig[status] || {
      variant: "secondary",
      text: status,
    };
    return (
      <Badge bg={config.variant} className="px-2 py-1">
        {config.text}
      </Badge>
    );
  };

  const columns: DataListColumn<Reserva>[] = [
    {
      label: "Cliente",
      size: 5,
      render: (reserva) => {
        const cliente = clientes.find((c) => c.id === reserva.clienteId);
        return (
          <div>
            <div className="fw-semibold">
              {reserva.clienteNome || "Cliente não encontrado"}
            </div>
            <small className="text-muted">{cliente?.telefone || "N/A"}</small>
          </div>
        );
      },
    },
    {
      label: "Nº Reserva",
      size: 2,
      render: (reserva) => (
        <div className="d-flex align-items-center">
          <Hash size={16} className="text-primary" />
          <span
            className="fw-semibold text-primary"
            style={{ fontFamily: "monospace" }}
          >
            {formatNumeroReserva(reserva.numeroReserva || "")}
          </span>
        </div>
      ),
    },
    {
      label: "Retirada",
      size: 2,
      render: (reserva) => formatDate(reserva.dataRetirada),
    },
    {
      label: "Status",
      size: 2,
      render: (reserva) => getStatusBadge(reserva.status),
    },
    {
      label: "",
      size: 1,
      render: (reserva) => (
        <Button
          btnColor="primary"
          onClick={(e) => {
            e.stopPropagation();
            onViewReserva(reserva);
          }}
        >
          <FaEye size={20} />
        </Button>
      ),
    },
  ];

  // Função para abrir WhatsApp
  const handleWhatsApp = (cliente: Cliente | null, reserva: Reserva) => {
    if (cliente) {
      window.open(
        `https://wa.me/55${cliente.telefone.replace(/\D/g, "")}`,
        "_blank",
      );
    }
  };

  return (
    <div className="reservas-calendar-sidebar">
      {/* Calendário à esquerda */}
      <div className="calendar-container">
        <div className="calendar-header">
          <h6 className="mb-3 fw-bold">Calendário de Reservas</h6>
        </div>
        <div className="calendar-wrapper">
          <SafeCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            onSelectSlot={handleSelectSlot}
            selectable
            view="month"
            views={["month"]}
            date={selectedDate}
            onNavigate={setSelectedDateSafe}
            dayPropGetter={dayPropGetter}
            style={{ height: "400px" }}
            step={1}
            timeslots={1}
            messages={{
              next: "Próximo",
              previous: "Anterior",
              today: "Hoje",
              month: "Mês",
              week: "Semana",
              day: "Dia",
              agenda: "Agenda",
              date: "Data",
              time: "Hora",
              event: "Evento",
              noEventsInRange: "Nenhuma reserva neste período",
              showMore: (total: number) => `+${total} mais`,
              allDay: "Dia todo",
              work_week: "Semana de trabalho",
            }}
          />
        </div>
      </div>

      {/* Lista de reservas à direita */}
      <div className="reservas-container">
        <div className="reservas-header">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              Reservas - {moment(selectedDate).format("DD/MM/YYYY")}
            </h5>
          </div>

          {/* Filtros */}
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <Form.Control
                type="text"
                placeholder="Buscar por cliente, número da reserva..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control-sm"
              />
            </div>
            <div className="col-md-6">
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-select-sm"
              >
                <option value="">Todos os status</option>
                <option value="reservado">Reservado</option>
                <option value="retirado">Retirado</option>
                <option value="devolvido">Devolvido</option>
                <option value="cancelado">Cancelado</option>
              </Form.Select>
            </div>
          </div>
        </div>

        {/* Lista de reservas */}
        <DataList
          columns={columns}
          data={paginatedReservas}
          rowKey={(reserva) => reserva.id!}
          disableHoverEffects={true}
          onRowClick={(reserva) => onViewReserva(reserva)}
          emptyComponent={
            <div className="text-center text-muted py-5">
              <div className="mb-3">
                <i className="fas fa-calendar-day fa-3x text-muted"></i>
              </div>
              <p className="mb-0">Nenhuma reserva para este dia</p>
            </div>
          }
        />

        {reservasDoDia.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={reservasDoDia.length}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => {
              setCurrentPage(page);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            onItemsPerPageChange={setItemsPerPage}
            showItemsPerPage={true}
            itemsPerPageOptions={[5, 10, 20, 50]}
            className="mt-3"
          />
        )}

        {/* Contador de reservas */}
        <div className="reservas-footer mt-3">
          <small className="text-muted">
            {reservasDoDia.length} reserva
            {reservasDoDia.length !== 1 ? "s" : ""} encontrada
            {reservasDoDia.length !== 1 ? "s" : ""}
          </small>
        </div>
      </div>
    </div>
  );
};

export default ReservasCalendarSidebar;
